import Modal from "components/blocks/Modal";
import {useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import ExternalExaminerSection from "./ExternalExaminerSection";
import useMutation from "hooks/useMutation";
import InternalExaminersTable from "./InternalExaminersTable";
import ExaminersDropDown from "./ExaminersDropDown";
import { UserResult } from "services/accountService/models/AccountType";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "components/blocks/DatePicker";
import {ExaminersService} from "../../../../../../../../../services/examinersService";
import {
    CreateCourseOrderExaminerRequest
} from "../../../../../../../../../services/examinersService/Model/ExaminerTypes";

export type SelectedExaminerType = Pick<UserResult, "arabicName" | "englishName" | "id" | "email" | "college">;
 type AddExaminerModalProps = {
  isOpen: boolean;
  onClose: (shouldUpdateTable?: boolean) => void;
  programOrderEvaluationId: number;
};

const AddExaminerModal = ({ isOpen, onClose, programOrderEvaluationId }: AddExaminerModalProps) => {
  const { t } = useTranslation(["Orders"]);

  const { control, register, setValue, getValues, formState: { errors } } = useForm<{ deadLine: Date | null; notes: string }>({
    defaultValues: {
      deadLine: new Date(new Date().setDate(new Date().getDate() + 1)),
      notes: ""
    }
  });

  const [selectedInternalExaminers, setSelectedInternalExaminers] = useState<SelectedExaminerType[]>([]);
  const [selectedExaminer, setSelectedExaminer] = useState<UserResult>();
  const [externalExaminersEmail, setExternalExaminersEmail] = useState<
    {
      email: string;
      isSentInvitation: boolean;
    }[]
  >([]);

  const { loading, mutateAsync } = useMutation({
    queryFn: async (request: CreateCourseOrderExaminerRequest[]) => {
      return await ExaminersService.addExaminer(request);
    },
  });

  const onAddExaminers = async () => {
    const examiners = selectedInternalExaminers.concat(externalExaminersEmail.map((u) => ({ email: u.email, id: "" })));

    const { deadLine, notes } = getValues();

    const newExaminers: CreateCourseOrderExaminerRequest[] = examiners.map((examiner) => ({
      examinerEmail: !examiner.id ? examiner.email : null,
      examinerId: examiner.id ? examiner.id : null,
       courseOrderEvaluationId: programOrderEvaluationId,
      deadLine: deadLine as Date,
      notes: notes || "",
    }));

    const res = await mutateAsync(newExaminers);

    if (!res?.hasError) {
      onModalClose(true);
    }
  };

  const onModalClose = (shouldUpdateExaminerTable: boolean) => {
    onClose(shouldUpdateExaminerTable);
    setSelectedInternalExaminers([]);
    setExternalExaminersEmail([]);
  };

  const modalRef = useRef<HTMLDivElement | null>(null);

  return (
    <Modal
      title={t("addExaminers")}
      isOpen={isOpen}
      onClose={() => onModalClose(false)}
      containerClassName="bg-white"
      size="lg"
      titleClassName="fs-6 text-dark"
    >
      <div ref={modalRef} className="d-flex flex-column gap-4">
        <ExaminersDropDown
          selectedExaminer={selectedExaminer}
          setSelectedExaminer={setSelectedExaminer}
          externalExaminersEmail={externalExaminersEmail}
          setExternalExaminersEmail={setExternalExaminersEmail}
          selectedInternalExaminers={selectedInternalExaminers}
          setSelectedInternalExaminers={setSelectedInternalExaminers}
        />

        <ExternalExaminerSection
          externalExaminersEmail={externalExaminersEmail}
          setExternalExaminersEmail={setExternalExaminersEmail}
        />

        <InternalExaminersTable
          selectedInternalExaminers={selectedInternalExaminers}
          setSelectedInternalExaminers={setSelectedInternalExaminers}
        />

        <div  className="divider"></div>
        <Controller
          name="deadLine"
          control={control}
          render={({ field }) => (
            <DatePicker
              portalTarget={modalRef.current}
              label={t("deadLine")}
              labelClassName="fw-bold text-dark  "
              placeholder="../../...."
              wrapperClassName="w-50"
              value={field.value || new Date()}
              error={errors.deadLine?.message}
              minDate={new Date()}
              onChange={(date) => {
                if (date) {
                  setValue("deadLine", date);
                }
              }}
            />
          )}
        />

        <div>
          <label className="fw-bold text-dark mb-1">{t("notes")}</label>
          <textarea
            className="form-control"
            placeholder={t("notes")}
            {...register("notes")}
            rows={5}
            style={{ resize: "none" }}
          />
        </div>

        <div className="d-flex justify-content-center gap-4">
          <button className={`btn btn-outline-secondary border rounded-3 p-10-32P`} onClick={() => onModalClose(false)}>
            {t("Common:cancel")}
          </button>
          <button
            className="btn btn-primary p-10-32P rounded-3"
            onClick={onAddExaminers}
            disabled={
              loading ||
              !programOrderEvaluationId ||
              (!selectedInternalExaminers.length && !externalExaminersEmail.length) ||
              !getValues().deadLine
            }
          >
            {!loading ? t("Common:add") : <div className="custom-loader"></div>}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddExaminerModal;
